import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useSettings } from "../lib/settings";
import { Calendar, MapPin, Gift, ExternalLink } from "lucide-react";
import { TEXT_DEFAULTS } from "../lib/siteTexts";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function EmbedTours() {
  const [params] = useSearchParams();
  const s = useSettings();
  const limit = parseInt(params.get("limit")) || 6;
  const mode = params.get("mode") || "calendar"; // "calendar" | "book"
  const [tours, setTours] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const siteUrl = window.location.origin;

  const brandColor = s.embed_brand_color || "#2D5A27";
  const bookLabel = s.embed_book_label || "Buchen";
  const loadingLabel = s.embed_loading_label || "Lade Touren…";
  const emptyTitle = s.embed_empty_title || "Die nächsten Termine folgen bald.";
  const emptyText = s.embed_empty_text || TEXT_DEFAULTS.tour_no_dates_text;
  const freeLabel = s.embed_free_label || "Kostenlos";

  useEffect(() => {
    api.get(`/embed/tours?limit=${limit}`)
      .then((r) => setTours(r.data || []))
      .finally(() => setLoaded(true));
  }, [limit]);

  if (!loaded) {
    return <div className="p-6 text-center text-sm opacity-60" data-testid="embed-loading">{loadingLabel}</div>;
  }

  if (tours.length === 0) {
    return (
      <div className="p-6 text-center" data-testid="embed-tours-empty">
        <div className="font-display text-xl mb-2">{emptyTitle}</div>
        <p className="text-sm opacity-70 max-w-md mx-auto">{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="embed-tours">
      <div className={mode === "book" ? "space-y-3" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"}>
        {tours.map((t) => {
          const isExt = !!t.external_booking_url;
          const isFree = t.is_free || t.price_eur === 0;
          const seats = (t.max_participants || 0) - (t.current_bookings || 0);
          const isSoldOut = !isExt && seats <= 0;
          const bookUrl = isExt ? t.external_booking_url : `${siteUrl}/tours/${t.tour_slug}`;
          return (
            <div key={t.id} className="rounded-xl overflow-hidden border bg-white shadow-sm" style={{ borderColor: "rgba(0,0,0,0.08)" }} data-testid={`embed-tour-${t.id}`}>
              {t.image_url && (
                <div className="relative aspect-[16/9]">
                  <img src={t.image_url} alt={t.image_alt || t.title} loading="lazy" className="w-full h-full object-cover" />
                  {isFree && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-700 text-white">
                      <Gift className="w-3 h-3" /> {freeLabel}
                    </div>
                  )}
                </div>
              )}
              <div className="p-3">
                <div className="font-semibold text-sm mb-1 line-clamp-2">{t.title}</div>
                <div className="text-xs text-gray-600 flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> {formatDate(t.date)}</div>
                {t.meeting_point && <div className="text-xs text-gray-600 flex items-center gap-1 mb-2" data-testid={`embed-meeting-${t.id}`}><MapPin className="w-3 h-3" /> Treffpunkt: {t.meeting_point}</div>}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs font-medium">{isSoldOut ? "Ausgebucht" : "Kostenlos"}</span>
                  {!isSoldOut && <a
                    href={bookUrl}
                    target="_top"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: brandColor }}
                    data-testid={`embed-book-${t.id}`}
                  >
                    {isExt ? <><ExternalLink className="w-3 h-3" /> {bookLabel}</> : bookLabel}
                  </a>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
